/**
 * PixiContainerRenderer — loot containers as flat shapes.
 *
 * One Graphics per container on the objects layer (above the static world
 * shapes, below entities). A container is redrawn only when its view key
 * changes — state, hidden flag, or a bucketed progress value — so an idle
 * chest costs nothing per frame and an opening/holding one animates at
 * ~30–60 steps. The renderer decides nothing: `containerView()` is the whole
 * mapping from game state to picture and is tested in Node.
 */
import * as PIXI from 'pixi.js';
import { containerView, containerViewKey, drawContainer } from './containerStyles.js';

const PixiContainerRenderer = {
  container: null,
  /** @type {Map<string, { g: PIXI.Graphics, key: string }>} */
  entries: new Map(),

  init(container) {
    this.container = container;
    this.entries = new Map();
  },

  /**
   * @param {object} gameState - reads worldObjects (objectType 'lootContainer'),
   *   lootContainerInteraction and serverNow()
   */
  update(gameState) {
    if (!this.container) return;
    const serverNow = typeof gameState.serverNow === 'function' ? gameState.serverNow() : Date.now();
    const interaction = gameState.lootContainerInteraction || null;
    const seen = new Set();

    for (const obj of gameState.worldObjects.values()) {
      if (obj.objectType !== 'lootContainer') continue;
      seen.add(obj.id);
      const hitsInfo = gameState.lootContainers?.get(obj.id) || null;
      const view = containerView(obj, interaction, serverNow, hitsInfo);
      const key = containerViewKey(view);
      let entry = this.entries.get(obj.id);
      if (!entry) {
        entry = { g: new PIXI.Graphics(), key: '' };
        this.container.addChild(entry.g);
        this.entries.set(obj.id, entry);
      }
      if (entry.key === key) continue;
      entry.key = key;
      entry.g.clear();
      drawContainer(entry.g, view);
    }

    for (const [id, entry] of this.entries) {
      if (!seen.has(id)) { this.container.removeChild(entry.g); entry.g.destroy(); this.entries.delete(id); }
    }
  },

  reset() {
    for (const { g } of this.entries.values()) { this.container?.removeChild(g); g.destroy(); }
    this.entries.clear();
  },

  destroy() {
    this.reset();
    this.container = null;
  }
};

export { PixiContainerRenderer };
